import type {Acl} from '@/providers/acl.types';
import type {Shop, UiSettings, Account} from '@myparcel-frontend/types';
import type {ComputedRef, Ref} from 'vue';
import {defineStore} from 'pinia';
import {computed, ref} from 'vue';
import useAcl from '@/composables/useAcl';
import useFeatures from '@/composables/useFeatures/useFeatures';
import {useEnvironmentStore} from '@/env/useEnvironmentStore';

export interface ConfigStore {
  account: Ref<Account>;
  uiSettings: Ref<UiSettings>;
  services: Ref<{
    appcuesIsReady: boolean;
  }>;
  getPlatformKey: ComputedRef<string>;
  frontofficeUrl: ComputedRef<string>;
  hasOrderMode: ComputedRef<boolean>;
  currentShop: ComputedRef<Shop>;
  currentUser: ComputedRef;
  shops: ComputedRef<Shop[]>;
  accountSettings: ComputedRef<Account['general_settings']>;
  appcuesIsReady: ComputedRef<boolean>;
  activeShops: ComputedRef<Shop[]>;
  accountFetchError: Ref<boolean>;
  setAcl: (acl: Acl) => void;
  setAccount: (account: Account) => void;
  setUiSettings: (uiSettings: UiSettings) => void;
  setAppcuesIsReady: (isReady: boolean) => void;
  updateShop: (shop: Shop) => void;
  insertShop: (shop: Shop) => void;
}

export const useConfigStore = defineStore('config', () => {
  const {environment} = useEnvironmentStore();

  const account = ref<Account>({});
  const uiSettings = ref<UiSettings>({});
  const services = ref({appcuesIsReady: false});
  const accountFetchError = ref(false);

  const {isFeatureActive} = useFeatures();
  const {acl} = useAcl();

  const currentShop = computed(
    () => account.value.shops?.find((shop: Shop) => shop.id === uiSettings.value.preferred_shop_id) as Shop,
  );

  const currentUser = computed(() => account.value.users.find(({id}: {id: number}) => id === acl.value.user_id));

  const shops = computed(() => account.value.shops);

  const activeShops = computed(() => {
    return account.value.shops.filter((shop) => {
      return !shop.hidden;
    });
  });

  const accountSettings = computed(() => account.value.general_settings);

  const appcuesIsReady = computed(() => services.value.appcuesIsReady);

  const frontofficeUrl = computed(() => {
    let baseUrl = environment.frontofficeUrl;

    /* istanbul ignore next 3 -cdk creates the url without a backslash at the end */
    if (!baseUrl.endsWith('/')) {
      baseUrl = `${baseUrl}/`;
    }

    if (!currentShop.value.branding?.subdomain) {
      if (!isFeatureActive('portal.events')) {
        return baseUrl.replace(':subdomain.', 'demo.');
      }

      return baseUrl.replace(':subdomain.', '');
    }

    return baseUrl.replace(':subdomain', currentShop.value.branding?.subdomain);
  });

  const setAccount = (newAccount: Account) => {
    account.value = newAccount;
  };

  const hasOrderMode = computed(() => !!accountSettings.value.order_mode || false);

  const setUiSettings = (newUiSettings: UiSettings) => {
    uiSettings.value = newUiSettings;
  };

  const setAppcuesIsReady = () => {
    services.value.appcuesIsReady = true;
  };

  const updateShop = (shop: Partial<Shop>) => {
    const index = account.value.shops.findIndex(({id}: {id: number}) => id === shop.id);

    if (index !== -1) {
      account.value.shops[index] = shop;
    }
  };

  const insertShop = (shop: Partial<Shop>) => {
    account.value.shops.push(shop);
  };

  return {
    account,
    uiSettings,
    services,
    currentShop,
    currentUser,
    shops,
    accountSettings,
    appcuesIsReady,
    activeShops,
    hasOrderMode,
    frontofficeUrl,
    accountFetchError,
    setAccount,
    setUiSettings,
    setAppcuesIsReady,
    updateShop,
    insertShop,
  };
});
